import { useEffect, useId, useRef } from "react";
import { IC } from "../constants/nav";
import { HeaderIcon, LogoutButton } from "./AppHeader";
import "./mobileHeaderShortcuts.css";

export const MOBILE_HEADER_SHORTCUTS = [
  { id: "clientes", label: "Clientes", description: "Contatos e histórico", icon: IC.people, color: "#72de99" },
  { id: "agenda", label: "Agenda", description: "Prazos e compromissos", icon: IC.calendar, color: "#7ed7ff" },
  { id: "impressoras", label: "Impressoras", description: "Máquinas e conexão", icon: IC.printer, color: "#bf9bff" },
  { id: "filamentos", label: "Filamentos", description: "Rolos, cores e estoque", icon: IC.spool, color: "#71e5ce" },
  { id: "materiais", label: "Materiais", description: "Insumos e acessórios", icon: IC.layers, color: "#edabf8" },
  { id: "notas", label: "Anotações", description: "Ideias e notas com IA", icon: IC.orders, color: "#ffd17c" },
  { id: "config", label: "Configurações", description: "Loja e preferências", icon: IC.settings, color: "#a7bdd6" },
  { id: "busca", label: "Buscar no app", description: "Encontre o que precisa", icon: "M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 003.09-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zM9.5 14a4.5 4.5 0 110-9 4.5 4.5 0 010 9z", color: "#84c2ff" },
];

export const isMobileShortcutTourTarget = (target) => MOBILE_HEADER_SHORTCUTS.some((item) => `nav-${item.id}` === target);

function useShortcutDialog(dialogRef, closeRef, onClose) {
  const closeAction = useRef(onClose);
  useEffect(() => { closeAction.current = onClose; }, [onClose]);
  useEffect(() => {
    const previous = document.activeElement;
    if (!document.querySelector(".tour-layer")) closeRef.current?.focus();
    const keydown = (event) => {
      if (document.querySelector(".tour-layer")) return;
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      if (dialogs.length && dialogs[dialogs.length - 1] !== dialogRef.current) return;
      if (event.key === "Escape") { event.preventDefault(); closeAction.current(); }
      if (event.key !== "Tab") return;
      const controls = [...dialogRef.current.querySelectorAll('button, input, select, textarea, a[href], [tabindex="0"]')]
        .filter((element) => !element.disabled && element.type !== "hidden");
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault(); first?.focus();
      }
    };
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("keydown", keydown);
      if (previous?.isConnected) previous.focus();
    };
  }, [dialogRef, closeRef]);
}

export function MobileHeaderShortcuts({ onClose, onAction, onLogout, nome, empresa, avatar, clientesBadge = 0, impressorasCount }) {
  const id = useId();
  const dialogRef = useRef(null), closeRef = useRef(null);
  useShortcutDialog(dialogRef, closeRef, onClose);
  const action = (destino) => { onClose(); onAction(destino); };
  return <div className="mobile-shortcuts-backdrop" onClick={onClose}>
    <section ref={dialogRef} className="mobile-shortcuts" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} onClick={(event) => event.stopPropagation()}>
      <header className="mobile-shortcuts-header">
        <div><span>SEU ESTÚDIO, ORGANIZADO</span><h2 id={`${id}-title`}>Atalhos</h2></div>
        <button type="button" ref={closeRef} className="mobile-shortcuts-close" aria-label="Fechar atalhos" onClick={onClose}><HeaderIcon name="close" size={18} /></button>
      </header>
      <div className="mobile-shortcuts-scroll">
        <p className="mobile-shortcuts-intro">Tudo para criar, produzir e vender.</p>
        <div className="mobile-shortcuts-grid">
          {MOBILE_HEADER_SHORTCUTS.map((item) => <button type="button" className="mobile-shortcut" key={item.id} style={{ "--shortcut-color": item.color }} data-tour={`nav-${item.id}`} onClick={() => action(item.id)}>
            <HeaderIcon name="arrow" size={15} className="mobile-shortcut-arrow" />
            <span className="mobile-shortcut-icon"><HeaderIcon name={item.id} size={24} />
              {item.id === "clientes" && clientesBadge > 0 && <span className="mobile-shortcut-badge" aria-label={`${clientesBadge} novidades`}>{clientesBadge > 99 ? "99+" : clientesBadge}</span>}
              {item.id === "impressoras" && impressorasCount === 0 && <span className="mobile-shortcut-dot" aria-label="Nenhuma impressora cadastrada" />}
            </span>
            <strong>{item.label}</strong><small>{item.description}</small>
          </button>)}
        </div>
        <footer className="mobile-shortcuts-account">
          <div className="mobile-shortcuts-profile"><span aria-hidden="true">{!avatar || avatar === "👤" ? "🧑‍💻" : avatar}</span><div><strong>{nome || "Minha conta"}</strong><small>{empresa || "Print Control"}</small></div></div>
          <LogoutButton onClick={() => { onClose(); onLogout(); }} />
        </footer>
      </div>
    </section>
  </div>;
}

// Os atalhos usam os mesmos módulos do desktop, sem perder a tela de origem.
export function MobileShortcutModal({ title, color = "#a7bdd6", badge, onClose, children }) {
  const id = useId();
  const dialogRef = useRef(null), closeRef = useRef(null);
  useShortcutDialog(dialogRef, closeRef, onClose);
  return <div className="mobile-shortcuts-backdrop mobile-shortcut-modal-backdrop" onClick={onClose}>
    <section ref={dialogRef} className="mobile-shortcut-modal" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} onClick={(event) => event.stopPropagation()}>
      <header className="mobile-shortcuts-header"><h2 id={`${id}-title`} style={{ color }}>{title}{badge > 0 && <span className="mobile-shortcut-modal-badge">{badge}</span>}</h2><button ref={closeRef} type="button" className="mobile-shortcuts-close" aria-label={`Fechar ${title}`} onClick={onClose}>✕</button></header>
      <div className="mobile-shortcut-modal-content">{children}</div>
    </section>
  </div>;
}
